/*  Shared  */
import { CellContext, Row } from '@tanstack/react-table';
import { ManagedPicker } from 'application/Shared/Components/Pickers';
import { ManagementState } from 'application/Shared/Constants';

/*  React  */
import React from 'react';

import { ColumnFilter } from '../Table/types';

/**
 * Configuration object for management stage filtering control.
 */
export const managementStageFilter: ColumnFilter<unknown> = {
    control: 'Checkbox',
    defaultOptions: [
        {
            options: [
                ManagementState.ManagedApplication,
                ManagementState.UnmanagedApplication
            ]
        }
    ]
};

/**
 * Filter function for management state data cells in a table
 * @param {Row<TData>} row - The row data containing the information
 * @param {string} columnId - The ID of the column being filtered
 * @param {string} filterValue - The filter value to match against
 * @returns {boolean} True if the row should be included in filtered results, false otherwise
 */
export function managementStateFilterFn<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string[]
): boolean {
    if (!filterValue || !filterValue?.length) return true;

    // filterValue can be [ClassificationStatus.Classified], [ClassificationStatus.Unlassified],
    // or [ClassificationStatus.Classified, ClassificationStatus.Unlassified],
    // in other words, === ['true', 'false'], or ['true'], or ['false']

    const managementStateValue = row.getValue(columnId);

    return (
        (managementStateValue === ManagementState.ManagedApplication &&
            filterValue.includes(ManagementState.ManagedApplication)) ||
        (managementStateValue === ManagementState.UnmanagedApplication &&
            filterValue.includes(ManagementState.UnmanagedApplication))
    );
}

/**
 * A cell component for displaying management state in a table
 * @template TData The type of data being displayed in the table
 * @param props The cell context props from react-table
 * @param props.cell The cell information containing the value
 * @param props.row The row information containing the original data
 * @param props.rest Additional props to pass to the ManagedPicker component
 * @returns JSX element containing a ManagedPicker component
 */
export const ManagementStateCell = <TData,>({
    cell,
    row,
    ...rest
}: CellContext<TData, unknown>): JSX.Element => {
    const managementStage = cell.getValue();
    return (
        <ManagedPicker
            // @ts-expect-error TData
            rerender={row.original.rerendered}
            shortLabel
            value={managementStage}
            visualOnly
            {...rest}
        />
    );
};
